import { CommandDefinition, defineCommand } from '../../../concepts/commands/CommandDefinition';
import { Attribute, Component, component, property } from '../../../concepts/objects';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { Enum } from '../../../platform/Enum';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { AutoBringToFront } from '../../canvas/entities/AutoBringToFront';
import { DropInfo, DropTarget } from '../../canvas/entities/DropTarget';
import { Physical, PotentialInteraction, RelativeTo } from '../../canvas/entities/Physical';
import { RectangularPhysical } from '../../canvas/entities/RectangularPhysical';
import { Resizable } from '../../canvas/entities/Resizable';
import { Snappable } from '../../canvas/entities/Snappable';
import { UIField } from '../../canvas/entities/UIField';
import { Participant } from '../../canvas/participants/Participant';
import { SmartContainer } from '../controls/SmartContainer';
import { Pingable } from '../presentation/Pingable';
import { dropZoneDrop, dropZonePeek } from './DropZoneCommands';
import { DropZoneLabelField } from './DropZoneLabelField';
import { DropZoneLayout } from './DropZoneLayout';
import { dropZonePropertyLayout } from './DropZonePropertyLayout';

const invocation = defineCommand(
    'dropZoneInvocation',
    null,
    (cc, [id, hint]: [string, string?]) => {
        if (hint === 'peek') {
            cc.dispatch(dropZonePeek, [id]);
        }
    },
    [],
);

const mutation = defineCommand(
    'dropZoneMutation',
    null,
    (cc, [id]: [string]) => {
        cc.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field', 'label'));
    },
    [],
);

const dropZoneDef = {
    name: 'DropZone',
    invocation,
    mutation,
    implements: [Physical, Snappable, Resizable, Pingable, DropTarget, SmartContainer],
    propertyLayout: dropZonePropertyLayout,
};

const dropZoneTransformHints = Object.freeze({
    scaleX: true,
    scaleY: true,
    rotate: false,
    translate: true,
    visible: true,
});

export const DropZoneUser = new Attribute<Participant, DropZone>('dropZoneUser', null);
export const DropZonePeekOpen = new Attribute<boolean, DropZone>('dropZonePeekOpen', false);

export const DropZoneMode = Enum({
    shared: 1,
    participant: 2,
});

@component(dropZoneDef)
export class DropZone extends RectangularPhysical implements DropTarget, SmartContainer {

    @property({ type: 'number', default: 140 })
    public width!: number;

    @property({ type: 'number', default: 180 })
    public height!: number;

    @property({ type: 'string' })
    public label!: string;

    @property({ type: 'string', default: '#f8f8f8' })
    public background!: string;

    @property({ type: 'number', default: DropZoneMode.shared })
    public mode: number;

    @property({ type: 'boolean' })
    public allowMultiple!: boolean;

    @property({ type: 'boolean' })
    public allowPeeking!: boolean;

    @property({ type: 'string' })
    public userId!: string;

    public get shouldSnap() {
        return true;
    }

    @memoUsing('label', 'mode', DropZoneUser)
    public get actualLabel() {
        const user = DropZoneUser.get(this);
        return this.mode === DropZoneMode.participant && !!user ? user.name : this.label;
    }

    @memoUsing('width', 'height', 'actualLabel', 'allowPeeking')
    public get layout() {
        return DropZoneLayout(this);
    }

    @memoUsing('layout', 'actualLabel')
    public get uiFields(): readonly UIField[] {
        return [DropZoneLabelField(this)];
    }

    public getPhysicalsNaturallyOrdered(): Physical[] {
        return this.polarize(AutoBringToFront).reverse();
    }

    public queryPipe(subject: Physical): CommandDefinition<[string, string]> {
        if (subject.is(AutoBringToFront)) {
            return dropZoneDrop;
        }
        return null;
    }

    public queryDrop(mpt: Point, subjects: Iterable<Component>): DropInfo {
        if (!this.localBounds.contains(this.toRelative(mpt))) return null;
        for (const s of subjects) {
            if (!s.is(AutoBringToFront)) return null;
        }
        return {
            target: this,
            guides: [{
                points: this.transform(RelativeTo.Model).apply(this.layout.base),
            }],
            command: dropZoneDrop,
        };
    }


    public queryTransformHints() {
        return dropZoneTransformHints;
    }

    public bounds(to: RelativeTo | Physical = RelativeTo.Parent): Rect {
        return Rect.fromPoints(this.transform(to).apply(this.localBounds.inflate([0, 0]).offset([0, -0])));
    }

    public testInteraction({ type, mpt }: PotentialInteraction): [boolean, string] {
        const { base, peek } = this.layout;
        const rpt = this.toRelative(mpt);
        if (base.contains(rpt)) {
            if (type === 'invoke' && peek.contains(rpt)) {
                return [true, 'peek'];
            }
            else if (type !== 'invoke') {
                return [true, 'default'];
            }
        }
        return [false, null];
    }
}

